// based on https://github.com/videojs/videojs-contrib-quality-levels/tree/v2.0.5/src

import videojs, { VideoJsPlayer } from "video.js";

interface IRepresentation {
    bandwidth: number;
    height: number;
    id: string;
    width: number;

    enabled: <T extends boolean>(enable?: T) => T;
}

export interface IQualityLevel extends Omit<IRepresentation, "bandwidth" | "enabled"> {
    bitrate: number;
    label: string;
    readonly enabled?: boolean;
    enabled_: IRepresentation["enabled"];
}

export interface IQualityLevels
    extends Omit<videojs.EventTarget, "addEventListener" | "dispatchEvent" | "removeEventListener"> {
    levels_: IQualityLevel[];
    selectedIndex_: number;
    readonly selectedIndex?: number;
    readonly length?: number;
    addQualityLevel: (representation: IRepresentation) => IQualityLevel;
    dispose: () => void;
    getQualityLevelById: (id: string) => IQualityLevel | null;
    removeQualityLevel: (qualityLevel: IQualityLevel) => IQualityLevel | null;
}

export function QualityLevel({ bandwidth, height, id, width, enabled }: IRepresentation) {
    const level: IQualityLevel = {
        bitrate: bandwidth,
        height: height,
        id: id,
        label: id,
        width: width,

        enabled_: enabled,
    };

    Object.defineProperty(level, "enabled", {
        /**
         * Get whether the QualityLevel is enabled.
         */
        get(): boolean {
            return level.enabled_();
        },

        /**
         * Enable or disable the QualityLevel.
         */
        set<T extends boolean>(enable: T): T {
            return level.enabled_(enable);
        },
    });
    return level;
}

/**
 * A list of QualityLevels.
 */
const QualityLevelList = (player: VideoJsPlayer): IQualityLevels => {
    const list: IQualityLevels = {
        ...player,
        levels_: [],
        selectedIndex_: -1,
        addQualityLevel,
        dispose,
        getQualityLevelById,
        removeQualityLevel,
    };

    /**
     * Get the index of the currently selected QualityLevel.
     */
    Object.defineProperty(list, "selectedIndex", {
        get(): number {
            return list.selectedIndex_;
        },
    });

    /**
     * Get the length of the list of QualityLevels.
     */
    Object.defineProperty(list, "length", {
        get(): number {
            return list.levels_.length;
        },
    });

    /**
     * Adds a quality level to the list.
     */
    function addQualityLevel(representation: IRepresentation): IQualityLevel {
        let qualityLevel = getQualityLevelById(representation.id);

        // Do not add duplicate quality levels
        if (qualityLevel) {
            return qualityLevel;
        }

        const index = list.levels_.length;

        qualityLevel = QualityLevel(representation);

        if (!("" + index in list)) {
            Object.defineProperty(list, index, {
                get() {
                    return list.levels_[index];
                },
            });
        }

        list.levels_.push(qualityLevel);

        list.trigger({
            qualityLevel,
            type: "addqualitylevel",
        } as any); // eslint-disable-line @typescript-eslint/no-explicit-any

        return qualityLevel;
    }

    /**
     * Removes a quality level from the list.
     */
    function removeQualityLevel(qualityLevel: IQualityLevel): IQualityLevel | null {
        let removed = null;

        for (let i = 0, l = list.length; i < l; i++) {
            if (list[i] === qualityLevel) {
                removed = list.levels_.splice(i, 1)[0];

                if (list.selectedIndex_ === i) {
                    list.selectedIndex_ = -1;
                } else if (list.selectedIndex_ > i) {
                    list.selectedIndex_--;
                }
                break;
            }
        }

        if (removed) {
            list.trigger({
                qualityLevel,
                type: "removequalitylevel",
            } as any); // eslint-disable-line @typescript-eslint/no-explicit-any
        }

        return removed;
    }

    /**
     * Searches for a QualityLevel with the given id.
     */
    function getQualityLevelById(id: string): IQualityLevel | null {
        return list.levels_.filter((level) => level.id === id)?.pop();
    }

    /**
     * Resets the list of QualityLevels to empty
     *
     * @method dispose
     */
    function dispose() {
        list.selectedIndex_ = -1;
        list.levels_.length = 0;
    }

    return list;
};

/**
 * change - The selected QualityLevel has changed.
 * addqualitylevel - A QualityLevel has been added to the QualityLevelList.
 * removequalitylevel - A QualityLevel has been removed from the QualityLevelList.
 */
QualityLevelList.allowedEvents_ = {
    change: "change",
    addqualitylevel: "addqualitylevel",
    removequalitylevel: "removequalitylevel",
};

// emulate attribute EventHandler support to allow for feature detection
for (const event in QualityLevelList.allowedEvents_) {
    QualityLevelList["on" + event] = null;
}

/**
 * Initialisation function for the qualityLevels plugin. Sets up the QualityLevelList and event handlers.
 */
const initPlugin = function (player, options) {
    const originalPluginFn = player.qualityLevels;

    const qualityLevelList = QualityLevelList(player);

    const disposeHandler = () => {
        qualityLevelList.dispose();
        player.qualityLevels = originalPluginFn;
        player.off("dispose", disposeHandler);
    };

    player.on("dispose", disposeHandler);

    player.qualityLevels = () => qualityLevelList;
    player.qualityLevels.VERSION = "__VERSION__";

    return qualityLevelList;
};

/**
 * A video.js plugin.
 *
 * @param {Object} options Plugin options object
 * @function qualityLevels
 */
export const qualityLevels = function (options: object) {
    return initPlugin(this, videojs.mergeOptions({}, options));
};

videojs.registerPlugin("qualityLevels", qualityLevels);

// Include the version number.
qualityLevels.VERSION = "__VERSION__";
